import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { LogOut, User, Moon, Sun } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_fundingbridge/artifacts/6f4haw8y_9161E8E4-2A18-454E-B39C-D1A5E84AD155.png';

const Layout = ({ children, title }) => {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getRoleBasePath = () => {
    if (user?.role === 'borrower') return '/borrower';
    if (user?.role === 'lsp') return '/lsp';
    if (user?.role === 'admin') return '/admin';
    return '/';
  };

  const getRoleBadgeColor = () => {
    if (user?.role === 'borrower') return 'bg-gradient-to-r from-blue-500 to-cyan-500';
    if (user?.role === 'lsp') return 'bg-gradient-to-r from-purple-500 to-pink-500';
    if (user?.role === 'admin') return 'bg-gradient-to-r from-orange-500 to-red-500';
    return 'bg-blue-500';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 transition-colors duration-300" data-testid="layout-container">
      {/* Header */}
      <header className="glass-effect border-b border-slate-200 dark:border-slate-700 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to={getRoleBasePath()} className="flex items-center space-x-3 group" data-testid="logo-link">
              <img 
                src={LOGO_URL} 
                alt="Our Cap Logo" 
                className="h-16 w-auto object-contain group-hover:scale-105 transition-transform"
              />
            </Link>

            {user && (
              <div className="flex items-center space-x-3">
                {/* Theme Toggle */}
                <Button
                  onClick={toggleTheme}
                  variant="outline"
                  size="sm"
                  className="p-2 hover-scale"
                  data-testid="theme-toggle"
                >
                  {isDark ? <Sun className="w-4 h-4 text-yellow-500" /> : <Moon className="w-4 h-4 text-slate-700" />}
                </Button>

                {/* User Info */}
                <div className="flex items-center space-x-3 px-4 py-2 glass-effect rounded-xl border border-slate-200 dark:border-slate-700 animate-slide-in-up">
                  <div className="w-8 h-8 bg-gradient-to-br from-slate-700 to-slate-500 dark:from-slate-600 dark:to-slate-400 rounded-lg flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{user.name}</p>
                    <span className={`text-xs px-2 py-0.5 ${getRoleBadgeColor()} text-white rounded font-medium`}>
                      {user.role.toUpperCase()}
                    </span>
                  </div>
                </div>

                {/* Logout Button */}
                <Button
                  onClick={handleLogout}
                  variant="outline"
                  size="sm"
                  className="flex items-center space-x-2 border-slate-300 dark:border-slate-700 hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-300 hover:text-red-600 transition-colors"
                  data-testid="logout-button"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Logout</span>
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {title && (
          <div className="mb-8 animate-slide-in-up">
            <h1 className="text-4xl font-bold text-slate-900 dark:text-slate-100">{title}</h1>
            <div className="h-1 w-20 gradient-primary rounded-full mt-2"></div>
          </div>
        )}
        {children}
      </main>

      {/* Footer */}
      <footer className="mt-16 glass-effect border-t border-slate-200 dark:border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center text-sm text-slate-600 dark:text-slate-400">
            <p className="mb-2">
              Our Cap is a coordination platform. We are not a lender and do not provide loan funds.
            </p>
            <p>&copy; {new Date().getFullYear()} Our Cap. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;